import { useEffect, useRef, useState, type JSX } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { ApiError, consumeMagicLink } from '../lib/api';
import { setToken } from '../lib/auth';

// Magic-link landing page. Reads ?t=<token>, posts to
// /v1/auth/sessions/consume, stashes the JWT, and forwards to /invoices.
const ConsumeSearch = z.object({ t: z.string().min(8).max(512) });

export const Route = createFileRoute('/auth/consume')({
  validateSearch: ConsumeSearch,
  component: ConsumePage,
});

function ConsumePage(): JSX.Element {
  const { t: token } = Route.useSearch();
  const navigate = useNavigate();
  const intl = useIntl();
  const [status, setStatus] = useState<'pending' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Magic-link tokens are single-use. React StrictMode invokes effects twice in
  // dev (setup → cleanup → setup); a `cancelled` flag isn't enough because the
  // first, successful consume would bail on cleanup and the second consume hits
  // an already-used token, stranding the user on this page. Guard so the
  // exchange runs exactly once per mount. No-op in production (single invoke).
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    async function run(): Promise<void> {
      try {
        const result = await consumeMagicLink(token);
        setToken(result.token);
        await navigate({ to: '/invoices', replace: true });
      } catch (err) {
        setStatus('error');
        if (err instanceof ApiError) {
          const body = err.body as { error?: string } | null;
          if (body?.error === 'MagicLinkExpired') {
            setErrorMessage(
              intl.formatMessage({
                id: 'auth.consume.error.expired',
                defaultMessage: 'This sign-in link has expired. Please request a new one.',
              }),
            );
            return;
          }
          if (body?.error === 'MagicLinkAlreadyUsed') {
            setErrorMessage(
              intl.formatMessage({
                id: 'auth.consume.error.used',
                defaultMessage: 'This sign-in link has already been used.',
              }),
            );
            return;
          }
          if (body?.error === 'InvalidMagicLink') {
            setErrorMessage(
              intl.formatMessage({
                id: 'auth.consume.error.invalid',
                defaultMessage: 'This sign-in link is invalid.',
              }),
            );
            return;
          }
        }
        setErrorMessage(
          intl.formatMessage({
            id: 'auth.consume.error.generic',
            defaultMessage: 'Sign-in failed. Please request a new link.',
          }),
        );
      }
    }
    void run();
  }, [token, navigate, intl]);

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-lg border border-navy-200 bg-white p-8 text-center shadow-sm">
        {status === 'pending' ? (
          <p className="text-sm text-navy-600">
            {intl.formatMessage({
              id: 'auth.consume.loading',
              defaultMessage: 'Signing you in...',
            })}
          </p>
        ) : (
          <div>
            <p className="text-sm text-[var(--color-danger)]" role="alert">
              {errorMessage}
            </p>
            <Link
              to="/login"
              className="mt-4 inline-block rounded-md border border-navy-200 px-3 py-1.5 text-sm text-navy-800 hover:bg-navy-50"
            >
              {intl.formatMessage({
                id: 'auth.consume.requestNew',
                defaultMessage: 'Request a new link',
              })}
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
